import type { MouseEvent } from 'react'

import clsx from 'clsx'

import { sanitizeEmbedHtml, sanitizeHtml } from '@/shared/lib/html/sanitize'
import { useInternalLinkClick } from '@/shared/hooks/useInternalLinkClick'
import { safeHref } from '@/shared/lib/url/safe-href'
import Image from '@/shared/ui/AppImage'
import { Button } from '@/shared/ui/Button'
import type { GamificationPageBlock } from '@/types/gamificationPageBlock'

interface BlockProps {
  block: GamificationPageBlock
}

const linkTarget = (block: GamificationPageBlock) =>
  block.settings?.target === '_blank' ? '_blank' : '_self'

const relFor = (target: string) =>
  target === '_blank' ? 'noopener noreferrer' : undefined

/** Link interno navega no roteador: `_blank` abriria fora da sessão do app. */
const useLinkProps = (block: GamificationPageBlock, href: string | undefined) => {
  const { onLinkClick, isInternal } = useInternalLinkClick()
  const target = isInternal(href) ? '_self' : linkTarget(block)
  return {
    target,
    rel: relFor(target),
    onClick: (event: MouseEvent) => onLinkClick(event, href)
  }
}

/** Imagem com link. `media_id` é FK real na api — não vive dentro de jsonb. */
export const BannerBlock = ({ block }: BlockProps) => {
  const href = safeHref(block.external_link)
  const link = useLinkProps(block, href)
  const url = block.media?.file?.url
  if (!url) return null

  const ratio = block.settings?.ratio ?? '16/9'
  // `auto`: arte sem proporção fixa. Sem caixa de aspect-ratio, a imagem ocupa
  // a largura toda e define a própria altura.
  const natural = ratio === 'auto'

  const image = (
    <Image
      src={url}
      alt={block.title ?? ''}
      width={1200}
      height={675}
      className={clsx(
        'w-full rounded-xl',
        natural ? 'h-auto' : 'h-full object-cover'
      )}
    />
  )

  return (
    <div
      className="overflow-hidden"
      style={natural ? undefined : { aspectRatio: ratio }}
    >
      {href ? (
        <a
          href={href}
          {...link}
          className={clsx('block w-full', !natural && 'h-full')}
        >
          {image}
        </a>
      ) : (
        image
      )}
    </div>
  )
}

/** Sanitizado também na gravação, na api. Aqui é a segunda barreira. */
export const RichTextBlock = ({ block }: BlockProps) => {
  const { onContentClick } = useInternalLinkClick()
  if (!block.text_content) return null

  return (
    <div
      onClick={onContentClick}
      className="wysiwyg-content text-base text-gray-900"
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(block.text_content) }}
    />
  )
}

/** Host do `src` é validado contra allowlist na api antes de gravar. */
export const EmbedBlock = ({ block }: BlockProps) => {
  if (!block.embed_code) return null

  const ratio = block.settings?.ratio ?? '16/9'

  return (
    <div
      className="overflow-hidden rounded-xl [&_iframe]:h-full [&_iframe]:w-full"
      style={{ aspectRatio: ratio }}
      dangerouslySetInnerHTML={{ __html: sanitizeEmbedHtml(block.embed_code) }}
    />
  )
}

export const CtaBlock = ({ block }: BlockProps) => {
  const href = safeHref(block.external_link)
  const link = useLinkProps(block, href)
  if (!block.title || !href) return null

  return (
    <a href={href} {...link} className="block no-underline"
    >
      <Button
        variant={(block.settings?.variant as any) ?? 'primary'}
        size="lg"
        className="w-full"
      >
        {block.title}
      </Button>
    </a>
  )
}

export const SectionTitleBlock = ({ block }: BlockProps) => {
  if (!block.title) return null

  const size = block.settings?.size ?? 'md'

  return (
    <h2
      className={clsx(
        'm-0 font-bold leading-tight text-gray-900',
        size === 'sm' && 'text-base',
        size === 'md' && 'text-lg',
        size === 'lg' && 'text-xl'
      )}
    >
      {block.title}
    </h2>
  )
}
